import maplibregl from "maplibre-gl";

/** Function signature for MapLibre internal render request methods. */
type RequestRenderFn = ( ...args: unknown[] ) => void;

/**
 * Handle containing information about the resolved render scheduler method.
 */
interface RequestRenderHandle {

    /** Name of the method on the map instance. */
    "methodName": string;

    /** Reference to the original method. */
    "method": RequestRenderFn;

    /** True if using triggerRepaint as a fallback. */
    "isFallback": boolean;
}

/**
 * Throttle MapLibre render scheduling to a maximum FPS.
 * It also supports pausing render requests when the document is hidden.
 */
export class MapRenderThrottler {

    /** The attached MapLibre map instance. */
    private map: maplibregl.Map | null = null;

    /** Reference to the original render scheduler method. */
    private originalRequestRender: RequestRenderFn | null = null;

    /** Name of the wrapped method on the map instance. */
    private methodName: string | null = null;

    /** Minimum interval between render requests (ms). */
    private minIntervalMs!: number;

    /** Timestamp of the last render request (ms). */
    private lastRenderRequestMs: number = 0;

    /** ID of the trailing timeout for delayed render requests. */
    private trailingTimeoutId: number | null = null;

    /** Whether rendering is currently paused. */
    private paused: boolean = false;

    /** Whether a render request is pending while paused or throttled. */
    private pending: boolean = false;

    /** Arguments from the last render request for replay. */
    private lastRequestArgs: unknown[] = [];

    /**
     * Create a new MapRenderThrottler instance.
     * @param maxFps - Maximum frames per second. Use 0 to disable throttling.
     */
    public constructor( maxFps: number ) {
        this.minIntervalMs = this.calculateMinInterval( maxFps );
    }

    /**
     * Attach the throttler to a MapLibre map instance.
     * Wraps the internal render scheduler to enforce the FPS limit.
     * @param map - The MapLibre map instance to throttle.
     */
    public attach( map: maplibregl.Map ): void {
        this.detach();
        this.map = map;

        const handle = this.resolveRequestRender( map );
        if ( handle === null ) {
            console.warn( "[MapRenderThrottler] Unable to find a render scheduler to wrap." );

            return;
        }

        this.methodName = handle.methodName;
        this.originalRequestRender = handle.method;

        const mapAny = map as unknown as Record<string, unknown>;
        mapAny[this.methodName] = this.throttledRequestRender as unknown;

        if ( handle.isFallback ) {
            console.warn( "[MapRenderThrottler] Using fallback method; throttling may be incomplete." );
        }
    }

    /**
     * Detach the throttler and restore the original render method.
     * Clears any pending timeouts and resets internal state.
     */
    public detach(): void {
        if ( this.map !== null && this.methodName !== null && this.originalRequestRender !== null ) {
            const mapAny = this.map as unknown as Record<string, unknown>;
            mapAny[this.methodName] = this.originalRequestRender as unknown;
        }

        this.clearTrailingTimeout();

        this.map = null;
        this.methodName = null;
        this.originalRequestRender = null;
        this.pending = false;
        this.lastRequestArgs = [];
        this.lastRenderRequestMs = 0;
    }

    /**
     * Update the maximum FPS limit.
     * @param maxFps - Maximum frames per second. Use 0 to disable throttling.
     */
    public setMaxFps( maxFps: number ): void {
        this.minIntervalMs = this.calculateMinInterval( maxFps );
    }

    /**
     * Pause or resume render requests.
     * When paused, render requests are queued and replayed when resumed.
     * @param paused - True to pause rendering, false to resume.
     */
    public setPaused( paused: boolean ): void {
        this.paused = paused;
        if ( paused ) {
            this.clearTrailingTimeout();

            return;
        }

        if ( this.pending ) {
            this.pending = false;
            this.handleRequestRender( this.lastRequestArgs );
        }
    }

    /**
     * Throttled render request handler bound to the map instance.
     * @param args - Arguments passed to the original render method.
     */
    private readonly throttledRequestRender = ( ...args: unknown[] ): void => {
        this.handleRequestRender( args );
    };

    /**
     * Handle a render request with throttling logic.
     * Enforces minimum interval between render calls and manages trailing requests.
     * @param args - Arguments to forward to the original render method.
     */
    private handleRequestRender( args: unknown[] ): void {
        if ( this.map === null || this.originalRequestRender === null ) {
            return;
        }

        this.lastRequestArgs = args;

        if ( this.paused ) {
            this.pending = true;

            return;
        }

        if ( this.minIntervalMs <= 0 ) {
            this.originalRequestRender.apply( this.map, args );

            return;
        }

        const now = performance.now();
        const elapsed = now - this.lastRenderRequestMs;
        if ( elapsed >= this.minIntervalMs ) {
            this.lastRenderRequestMs = now;
            this.pending = false;
            this.originalRequestRender.apply( this.map, args );

            return;
        }

        this.pending = true;
        if ( this.trailingTimeoutId !== null ) {
            return;
        }

        const delay = Math.max( this.minIntervalMs - elapsed, 0 );
        this.trailingTimeoutId = window.setTimeout( () => {
            this.trailingTimeoutId = null;
            if ( this.map === null || this.originalRequestRender === null ) {
                return;
            }
            if ( this.paused ) {
                this.pending = true;

                return;
            }

            this.pending = false;
            this.lastRenderRequestMs = performance.now();
            this.originalRequestRender.apply( this.map, this.lastRequestArgs );
        }, delay );
    }

    /**
     * Resolve the internal render scheduler method from the map instance.
     * Uses triggerRepaint which is the actual method that schedules frames via requestAnimationFrame.
     *
     * Note: In MapLibre GL JS v5+, triggerRepaint() is the method that actually schedules render frames.
     * Internal methods like _requestRenderFrame only add callbacks to a queue but don't control rendering.
     *
     * @param map - The MapLibre map instance.
     * @returns Handle containing the method name and function, or null if not found.
     */
    private resolveRequestRender( map: maplibregl.Map ): RequestRenderHandle | null {
        /*
         * triggerRepaint is the correct method to wrap - it's what actually schedules frames
         * via requestAnimationFrame and calls _render()
         */
        if ( typeof map.triggerRepaint === "function" ) {
            return {
                "methodName": "triggerRepaint",
                "method":     map.triggerRepaint.bind( map ) as RequestRenderFn,
                "isFallback": false,
            };
        }

        // Fallback to internal methods for older MapLibre versions
        const mapAny = map as unknown as Record<string, unknown>;
        const candidates: string[] = [
            "_requestRender",
            "_requestRenderFrame",
            "_render",
        ];

        for ( const candidate of candidates ) {
            const method = mapAny[candidate];
            if ( typeof method === "function" ) {
                console.warn( `[MapRenderThrottler] triggerRepaint not found, falling back to ${ candidate }` );

                return {
                    "methodName": candidate,
                    "method":     method as RequestRenderFn,
                    "isFallback": true,
                };
            }
        }

        return null;
    }

    /**
     * Calculate the minimum interval between render requests.
     * @param maxFps - Maximum frames per second. Use 0 or negative for no limit.
     * @returns Minimum interval in milliseconds, or 0 if unlimited.
     */
    private calculateMinInterval( maxFps: number ): number {
        if ( maxFps <= 0 ) {
            return 0;
        }

        return 1000 / maxFps;
    }

    /**
     * Clear any pending trailing timeout.
     */
    private clearTrailingTimeout(): void {
        if ( this.trailingTimeoutId !== null ) {
            window.clearTimeout( this.trailingTimeoutId );
            this.trailingTimeoutId = null;
        }
    }

}
